import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { chatService, ChatRoom, ChatMessage, ChatRoomMember, ChatAttachment } from '../services/chatService';
import { supabase } from '../lib/supabase';
import { chatNotificationService } from '../services/notificationService';
import { moderationService } from '../services/moderationService';
import { ChatPopupContext } from './ChatPopupContext';
import { aplicarMensagemAtualizada } from '../components/Chat/messageEditing';
import { mensagemMencionaUsuario } from '../components/Chat/mentionMatch';

interface ChatContextType {
  rooms: ChatRoom[];
  messages: ChatMessage[];
  error: string | null;
  loadRooms: () => Promise<void>;
  createRoom: (
    name: string,
    type: ChatRoom['type'],
    projectId?: string
  ) => Promise<ChatRoom | null>;
  currentRoom: ChatRoom | null;
  members: ChatRoomMember[];
  isLoading: boolean;
  selectRoom: (roomId: string) => Promise<void>;
  clearCurrentRoom: () => void;
  sendMessage: (content: string, parentId?: string, mentionedUserIds?: string[]) => Promise<void>;
  sendMessageWithAttachment: (content: string, file: File, parentId?: string) => Promise<void>;
  editMessage: (messageId: string, content: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  addMember: (userAuthId: string, role?: 'admin' | 'member' | 'guest') => Promise<void>;
  removeMember: (userAuthId: string) => Promise<void>;
  updateMemberRole: (userAuthId: string, newRole: 'admin' | 'member' | 'guest') => Promise<void>;
  isCurrentUserAdmin: () => Promise<boolean>;
}

// Quais salas o Realtime deve observar. `roomIdsDoUsuario` nulo significa "ainda
// não sei" (a consulta de participação não respondeu ou falhou) e lista vazia é
// igualmente suspeita — RLS em chat_room_members devolve zero linhas sem erro
// nenhum. Nos dois casos vale tudo: ficar sem notificação por causa de uma
// consulta silenciosa é pior que observar salas demais, ainda mais agora que a
// escuta inteira cabe em um único canal. Exportada para teste.
export function selectSubscribableRooms(
  rooms: ChatRoom[],
  roomIdsDoUsuario: string[] | null
): ChatRoom[] {
  if (!roomIdsDoUsuario || roomIdsDoUsuario.length === 0) return rooms;
  const permitidas = new Set(roomIdsDoUsuario);
  const filtradas = rooms.filter(room => permitidas.has(room.id));
  return filtradas.length > 0 ? filtradas : rooms;
}

// Encaixa numa mensagem já carregada o anexo que chegou pelo Realtime depois
// dela. O anexo nasce num INSERT posterior ao da mensagem — é outra tabela — e
// quem estava com a sala aberta recebe o aviso da mensagem antes de a linha do
// anexo existir. Sem isto, essa mensagem fica sem o anexo até recarregar.
// Devolve a lista original quando não há o que mudar, para não remontar a lista
// de mensagens à toa. Exportada para teste.
export function applyAttachmentToMessages(
  messages: ChatMessage[],
  attachment: ChatAttachment
): ChatMessage[] {
  const alvo = messages.find(msg => msg.id === attachment.message_id);
  if (!alvo) return messages;
  if (alvo.attachments?.some(anexo => anexo.id === attachment.id)) return messages;

  return messages.map(msg =>
    msg.id === attachment.message_id
      ? { ...msg, attachments: [...(msg.attachments || []), attachment] }
      : msg
  );
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: React.ReactNode;
  organizationId?: string;
}

export function ChatProvider({ children, organizationId: propOrgId }: ChatProviderProps) {
  const { user } = useAuth();
  
  // useCallback com dep em `user?.id`, não recriada a cada render — como é
  // dependência de praticamente todo useCallback deste arquivo, uma referência
  // nova a cada render (função comum) fazia CADA um deles mudar de identidade
  // a cada render do ChatProvider, disparando em cascata efeitos que dependem
  // deles (ex: checkAdminStatus em ChatRoom.tsx rodando dezenas de vezes por
  // segundo enquanto havia atividade no chat).
  const getUserAuthId = useCallback((): string | null => {
    if (!user) {
      console.error('ChatContext: No user available');
      return null;
    }
    return user.id || null;
  }, [user?.id]);
  
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [currentRoom, setCurrentRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [members, setMembers] = useState<ChatRoomMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messagesPage, setMessagesPage] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);

  // ChatPopupContext nem sempre está disponível (ex: instância inerte de ChatProvider
  // fora do OrganizationProvider), por isso usamos useContext direto em vez de useChatPopup().
  const chatPopup = useContext(ChatPopupContext);
  // chatPopup muda de referência a cada mensagem (unreadByRoom muda). Se entrasse direto
  // nas deps dos efeitos abaixo, cada mensagem recriaria a subscription global inteira
  // (unsubscribe + resubscribe no Supabase Realtime) — daí a lentidão. Usamos ref.
  const chatPopupRef = useRef(chatPopup);
  useEffect(() => {
    chatPopupRef.current = chatPopup;
  }, [chatPopup]);

  // Refs para evitar recriar a subscription global a cada mudança de sala/usuário
  const currentRoomIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentRoomIdRef.current = currentRoom?.id ?? null;
  }, [currentRoom]);

  // A subscription global só precisa ser refeita quando o CONJUNTO de salas muda.
  // Dependendo do array, qualquer recarga que devolvesse a mesma lista derrubava e
  // recriava todos os canais, e mensagens que chegassem nessa janela se perdiam.
  const roomsRef = useRef<ChatRoom[]>(rooms);
  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);
  // Só as salas em que o usuário participa merecem um canal Realtime. `null`
  // significa "ainda não sei" (ou a consulta falhou) e, nesse caso, mantemos o
  // comportamento antigo de assinar tudo, pra nunca ficar sem notificação.
  const [subscribableRoomIds, setSubscribableRoomIds] = useState<string[] | null>(null);

  useEffect(() => {
    const authId = user?.id;
    if (!authId) {
      setSubscribableRoomIds(null);
      return;
    }

    let cancelado = false;
    chatService.getRoomIdsForUser(authId)
      .then(ids => {
        if (!cancelado) setSubscribableRoomIds(ids);
      })
      .catch(err => {
        console.error('ChatContext: Erro ao carregar salas do usuário:', err);
        if (!cancelado) setSubscribableRoomIds(null);
      });

    return () => { cancelado = true; };
  }, [user?.id]);

  const subscribedRooms = useMemo(
    () => selectSubscribableRooms(rooms, subscribableRoomIds),
    [rooms, subscribableRoomIds]
  );

  const subscribedRoomsRef = useRef<ChatRoom[]>(subscribedRooms);
  useEffect(() => {
    subscribedRoomsRef.current = subscribedRooms;
  }, [subscribedRooms]);

  const roomsKey = useMemo(
    () => subscribedRooms.map(room => room.id).sort().join(','),
    [subscribedRooms]
  );

  const userAuthIdRef = useRef<string | null>(null);
  const userEmailRef = useRef<string | undefined>(undefined);
  // O autocomplete de menção insere o NOME DE EXIBIÇÃO (`@Maria Silva`), então
  // reconhecer que fui mencionado depende de saber o meu nome, não só o apelido
  // do e-mail (ver components/Chat/mentionMatch.ts).
  const userDisplayNameRef = useRef<string | null>(null);
  useEffect(() => {
    userAuthIdRef.current = user?.id ?? null;
    userEmailRef.current = user?.email;
    // Depende dos campos usados, não do objeto `user` inteiro — o Supabase emite uma
    // referência nova para a mesma pessoa sempre que a aba volta a ficar visível.
  }, [user?.id, user?.email]);

  useEffect(() => {
    const authId = user?.id;
    if (!authId) {
      userDisplayNameRef.current = null;
      return;
    }

    let cancelado = false;
    chatService.getDisplayName(authId)
      .then(nome => { if (!cancelado) userDisplayNameRef.current = nome; })
      .catch(() => { /* sem nome, a menção ainda casa pelo apelido do e-mail */ });

    return () => { cancelado = true; };
  }, [user?.id]);

  // Não pedir permissão aqui: pedido feito no carregamento da página, longe de
  // um clique, e descartado pelo navegador — e queimava a chance de perguntar
  // de novo. Quem pede é o clique no botão do chat. Aqui só registramos o
  // estado, pra "não chega notificação" parar de ser um mistério silencioso.
  useEffect(() => {
    if (!user) return;

    const permissao = chatNotificationService.permissionStatus();
    if (permissao !== 'granted') {
      console.warn(
        `ChatContext: notificações de desktop indisponíveis (permissão: ${permissao}). ` +
        'Abra o chat pelo botão flutuante para autorizar.'
      );
    }
  }, [user?.id]);

  // Subscription global: um canal por sala do usuário (mesmo padrão comprovado usado em
  // OrganizationContext/ProjectOverview e no canal de membros abaixo — filtro `eq`, não `in`,
  // que não tem uso comprovado nesse servidor Realtime e pode simplesmente não entregar nada).
  // Alimenta o badge de não lidas por sala e os alertas de menção, independente da sala aberta.
  useEffect(() => {
    if (subscribedRoomsRef.current.length === 0) {
      // Sem sala assinada não existe notificação nenhuma: isso é falha, não
      // repouso, e precisa aparecer no console em vez de sair em silêncio.
      console.warn(
        'ChatContext: nenhuma sala para assinar — notificações de chat ficam desligadas.',
        { salasCarregadas: roomsRef.current.length }
      );
      return;
    }

    const handleNewMessage = async (roomId: string, newRow: { id: string; user_auth_id: string; content: string }) => {
      const myAuthId = userAuthIdRef.current;
      if (!myAuthId || newRow.user_auth_id === myAuthId) return;

      const isSelectedRoom = currentRoomIdRef.current === roomId;
      // "Sendo vista agora" exige o popup aberto e maximizado, não só a sala
      // selecionada — sem isso, fechar/minimizar o chat numa sala já selecionada
      // continuava contando como "estou vendo essa sala", e o badge/notificação
      // nunca disparavam de novo pra ela enquanto o chat estivesse fechado.
      const isActivelyViewing = isSelectedRoom && !!chatPopupRef.current?.isOpen && !chatPopupRef.current?.isMinimized;

      // Badge e menção saem do PRÓPRIO evento, antes de qualquer ida ao
      // servidor. Antes tudo pendurava no fetch abaixo, e um `return` quando a
      // mensagem não era encontrada apagava badge, toast e som de uma vez —
      // silêncio total, sem nem um erro no console.
      const isTextMention = mensagemMencionaUsuario(newRow.content, {
        nome: userDisplayNameRef.current,
        email: userEmailRef.current
      });

      if (!isActivelyViewing) {
        chatPopupRef.current?.incrementUnreadCount(roomId);
      }
      if (isTextMention) {
        chatPopupRef.current?.setHasNewMentions(true);
      }

      let fullMessage: ChatMessage | undefined;
      try {
        // Pegar as últimas mensagens e localizar a do evento pelo id: buscar só
        // uma perderia mensagens quando duas chegam quase juntas, porque os dois
        // eventos veriam a mesma "mais recente".
        const mensagensRecentes = await chatService.getChatMessages(roomId, 10, 0);
        fullMessage = mensagensRecentes.find(msg => msg.id === newRow.id);
      } catch (err) {
        console.error('ChatContext: Erro ao carregar a mensagem recebida (subscription global):', err);
      }

      if (fullMessage) {
        const mensagemCompleta = fullMessage;
        // Mantém a prévia da lista de conversas em dia: o resumo das salas é
        // resolvido uma vez por organização e fica em cache.
        chatPopupRef.current?.registerRoomMessage(
          roomId,
          mensagemCompleta.content,
          mensagemCompleta.created_at || new Date().toISOString()
        );

        if (isSelectedRoom) {
          setMessages(prev =>
            prev.some(msg => msg.id === mensagemCompleta.id) ? prev : [...prev, mensagemCompleta]
          );
        }
      }

      // Responder a uma mensagem minha deve notificar como uma menção,
      // mesmo sem @mencionar meu nome no texto.
      const isReplyToMe = fullMessage?.reply_to_message?.user_auth_id === myAuthId;
      const isMention = isTextMention || isReplyToMe;

      if (isReplyToMe && !isTextMention) {
        chatPopupRef.current?.setHasNewMentions(true);
      }

      // Toca som/notificação desktop e mostra um toast na tela pra qualquer
      // mensagem nova de sala que não está sendo vista no momento, e sempre
      // que for uma menção (mesmo na sala aberta).
      if (!isActivelyViewing || isMention) {
        const room = roomsRef.current.find(r => r.id === roomId);
        const remetente = fullMessage?.user?.name || 'Usuário';
        const conteudo = fullMessage?.content ?? newRow.content;
        // O nome gravado na sala direta é genérico ("Conversa privada") e o
        // mesmo para os dois lados; quem mandou já aparece como remetente.
        const nomeDaSala = room?.type === 'direct' ? 'Mensagem privada' : room?.name || 'Chat';

        chatNotificationService.processNewMessage(
          nomeDaSala,
          remetente,
          conteudo,
          isMention ? [myAuthId] : []
        );
        chatPopupRef.current?.pushToast({
          roomId,
          roomName: nomeDaSala,
          senderName: remetente,
          content: conteudo,
          type: isMention ? 'mention' : 'message'
        });
      }
    };

    // UM canal para a tabela inteira, filtrando a sala no cliente. Antes era um
    // canal por sala: como a policy de chat_rooms libera todas as salas da
    // organização, abrir o chat disparava dezenas de `join` de uma vez, o que
    // estoura a cota de eventos da conexão e faz o servidor derrubá-la antes de
    // qualquer canal se estabelecer — nenhum evento chegava, nunca.
    const salasAssinadas = new Set(subscribedRoomsRef.current.map(room => room.id));

    // Conversa privada aberta depois que o app carregou — por mim ou por
    // alguém comigo — não está em `salasAssinadas`, e sem isto toda mensagem
    // dela era descartada aqui. Sala desconhecida é conferida uma vez: se for
    // direta e eu estiver nela, passa a ser ouvida. As de projeto alheias (o
    // Realtime entrega as da organização inteira) ficam no cache negativo.
    const salasIgnoradas = new Set<string>();
    const descobertas = new Map<string, Promise<boolean>>();
    const descobrirSalaDireta = (roomId: string): Promise<boolean> => {
      const emAndamento = descobertas.get(roomId);
      if (emAndamento) return emAndamento;

      const busca = (async () => {
        const myAuthId = userAuthIdRef.current;
        if (!myAuthId) return false;
        try {
          const room = await chatService.getChatRoom(roomId);
          if (room?.type !== 'direct') return false;
          if (!(await chatService.isRoomMember(roomId, myAuthId))) return false;

          salasAssinadas.add(roomId);
          // Só para o nome nas notificações; `subscribableRoomIds` fica como
          // está para não recriar este canal (e perder eventos na troca).
          setRooms(prev => (prev.some(r => r.id === roomId) ? prev : [...prev, room]));
          return true;
        } catch (err) {
          console.error('ChatContext: Erro ao conferir sala nova do Realtime:', err);
          return false;
        } finally {
          descobertas.delete(roomId);
        }
      })().then(ok => {
        if (!ok) salasIgnoradas.add(roomId);
        return ok;
      });

      descobertas.set(roomId, busca);
      return busca;
    };

    const channel = supabase
      .channel('chat-messages-watch')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => {
          const novaLinha = payload.new as {
            id: string;
            room_id: string;
            user_auth_id: string;
            content: string;
          };
          if (!novaLinha?.room_id) return;
          if (salasAssinadas.has(novaLinha.room_id)) {
            handleNewMessage(novaLinha.room_id, novaLinha);
            return;
          }
          if (salasIgnoradas.has(novaLinha.room_id)) return;
          void descobrirSalaDireta(novaLinha.room_id).then(ouvir => {
            if (ouvir) handleNewMessage(novaLinha.room_id, novaLinha);
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'chat_messages' },
        (payload) => {
          // Editar e excluir são UPDATE (a exclusão é lógica — ver
          // messageEditing.ts). Sem este handler, a correção de quem escreveu
          // só aparecia para os outros ao recarregar a sala.
          const linha = payload.new as Partial<ChatMessage> & { id?: string; room_id?: string };
          if (!linha?.id || !linha.room_id) return;
          // A sala aberta sempre vale, mesmo uma direta ainda não descoberta.
          if (!salasAssinadas.has(linha.room_id) && linha.room_id !== currentRoomIdRef.current) return;
          setMessages(prev => aplicarMensagemAtualizada(prev, linha as { id: string }));
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_attachments' },
        (payload) => {
          const anexo = payload.new as ChatAttachment;
          if (!anexo?.message_id) return;
          setMessages(prev => applyAttachmentToMessages(prev, anexo));
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_message_reactions' },
        (payload) => {
          const linha = (payload.new ?? payload.old) as { message_id?: string } | null;
          const messageId = linha?.message_id;
          if (!messageId) return;

          // Recarregar as reações da mensagem afetada em vez de aplicar o delta:
          // insert, troca de emoji e remoção chegam como eventos diferentes, e
          // reconstruir a lista a partir do banco não tem como divergir.
          void chatService
            .getReactionsForMessages([messageId])
            .then(reactions => {
              setMessages(prev =>
                prev.some(msg => msg.id === messageId)
                  ? prev.map(msg => (msg.id === messageId ? { ...msg, reactions } : msg))
                  : prev
              );
            })
            .catch(err => console.error('ChatContext: Erro ao atualizar reações:', err));
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log(`ChatContext: Realtime conectado, ouvindo ${salasAssinadas.size} sala(s)`);
          return;
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('ChatContext: Realtime do chat indisponível:', status, err ?? '');
        }
      });

    return () => {
      // `unsubscribe()` sozinho deixa o canal registrado no cliente: os órfãos
      // se acumulam a cada recriação e passam a competir pelo limite de canais
      // da conexão, que é onde os eventos começam a atrasar ou sumir.
      supabase.removeChannel(channel);
    };
  }, [roomsKey]);

  // Não lidas vindas do banco. Sem isto o badge do botão flutuante só conhecia
  // os eventos Realtime desta sessão: mensagem recebida com o app fechado, ou
  // qualquer F5, deixava a bolinha limpa mesmo havendo conversa por ler.
  // Recarrega também quando a aba volta ao foco, que é quando o usuário tende a
  // olhar o badge depois de um tempo longe.
  useEffect(() => {
    if (!user?.id) return;

    let cancelado = false;

    const sincronizar = () => {
      chatService.getUnreadCounts()
        .then(porSala => {
          if (cancelado) return;
          // A sala aberta na frente do usuário não pode reacender o badge:
          // `markMessagesAsRead` é assíncrono e o banco pode ainda não ter visto
          // a leitura que acabou de acontecer.
          const lendoAgora =
            chatPopupRef.current?.isOpen && !chatPopupRef.current?.isMinimized
              ? [currentRoomIdRef.current].filter((id): id is string => !!id)
              : [];
          chatPopupRef.current?.hidratarNaoLidas(porSala, lendoAgora);
        })
        .catch(err => console.error('ChatContext: Erro ao sincronizar não lidas:', err));
    };

    sincronizar();

    const aoVoltarPraAba = () => {
      if (document.visibilityState === 'visible') sincronizar();
    };
    document.addEventListener('visibilitychange', aoVoltarPraAba);

    return () => {
      cancelado = true;
      document.removeEventListener('visibilitychange', aoVoltarPraAba);
    };
  }, [user?.id]);

  // Limpar o contador de não lidas da sala atual ao reabrir/maximizar o popup
  // nela sem trocar de sala (selectRoom só roda numa seleção manual — sem isso,
  // reabrir o chat numa sala já selecionada deixava o badge preso).
  useEffect(() => {
    if (currentRoom && chatPopup?.isOpen && !chatPopup?.isMinimized) {
      chatPopup.clearRoomUnread(currentRoom.id);
    }
  }, [currentRoom, chatPopup?.isOpen, chatPopup?.isMinimized, chatPopup?.clearRoomUnread]);

  const loadRooms = useCallback(async () => {
    if (!propOrgId) {
      setError('Organization ID is required');
      return;
    }

    try {
      const rooms = await chatService.getChatRooms(propOrgId);
      setRooms(rooms);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chat rooms');
      console.error('Error loading chat rooms:', err);
    }
  }, [propOrgId]);

  useEffect(() => {
    if (propOrgId) {
      console.log('ChatContext: Organization ID changed, reloading rooms');
      loadRooms();
    } else {
      console.log('ChatContext: No organization ID available');
      setRooms([]);
    }
  }, [propOrgId, loadRooms]);

  // Função para limpar subscription anterior
  const cleanupSubscription = useCallback(() => {
    if (subscription) {
      console.log('ChatContext: Limpando subscription anterior');
      subscription.unsubscribe();
      setSubscription(null);
    }
  }, [subscription]);

  const selectRoom = useCallback(async (roomId: string) => {
    const userAuthId = getUserAuthId();
    if (!userAuthId) {
      console.error('ChatContext: No user auth ID available');
      throw new Error('Usuário não autenticado');
    }
    
    try {
      console.log('ChatContext: Iniciando seleção da sala:', roomId);
      setIsLoading(true);
      setError(null);
      
      // Limpar subscription anterior se houver
      cleanupSubscription();

      // Checar acesso, dados da sala, mensagens e membros não dependem uns
      // dos outros (RLS já protege cada tabela independentemente) — buscar
      // tudo em paralelo em vez de esperar cada etapa terminar pra começar a
      // próxima, que é o que fazia abrir uma sala levar ~3s mesmo com poucas
      // mensagens.
      console.log('ChatContext: Verificando acesso e carregando sala em paralelo');
      const [
        { data: memberData, error: memberError },
        roomData,
        messagesData,
        membersData
      ] = await Promise.all([
        supabase
          .from('chat_room_members')
          .select('role')
          .eq('room_id', roomId)
          .eq('user_auth_id', userAuthId)
          .single(),
        chatService.getChatRoom(roomId),
        chatService.getChatMessages(roomId),
        chatService.getChatRoomMembers(roomId)
      ]);

      if (memberError && memberError.code !== 'PGRST116') {
        console.error('ChatContext: Erro ao verificar membro:', memberError);
        throw new Error('Erro ao verificar acesso à sala');
      }

      if (!memberData) {
        console.log('ChatContext: Usuário não é membro da sala');
        throw new Error('Você não tem acesso a esta sala');
      }

      if (!roomData) {
        console.error('ChatContext: Sala não encontrada:', roomId);
        throw new Error('Sala não encontrada ou sem acesso');
      }

      console.log('ChatContext: Sala encontrada:', roomData);
      setCurrentRoom(roomData);

      setMessagesPage(0);
      setHasMoreMessages(true);

      console.log('ChatContext: Mensagens carregadas:', messagesData.length);
      setMessages(messagesData);

      console.log('ChatContext: Membros carregados:', membersData.length);
      setMembers(membersData);

      if (messagesData.length > 0) {
        // Não bloqueia a UI — marcar como lida não precisa atrasar a sala aparecer.
        // A última lida é a mais recente da página, que fecha o array em ordem cronológica.
        chatService.markMessagesAsRead(roomId, userAuthId, messagesData[messagesData.length - 1].id)
          .catch(err => console.error('ChatContext: Erro ao marcar mensagens como lidas:', err));
      }

      // Limpar contador de não lidas desta sala (a subscription global cuida das demais)
      chatPopupRef.current?.clearRoomUnread(roomId);

      // Configurar real-time subscription para membros da sala.
      // Novas mensagens já são tratadas pela subscription global (ver useEffect acima),
      // que cobre todas as salas do usuário, não só esta.
      console.log('ChatContext: Configurando subscription real-time para sala:', roomId);
      const newSubscription = supabase
        .channel(`chat-room-${roomId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'chat_room_members',
            filter: `room_id=eq.${roomId}`
          },
          async (payload) => {
            console.log('ChatContext: Novo membro adicionado via real-time:', payload.new);
            
            // Recarregar lista de membros
            try {
              const updatedMembers = await chatService.getChatRoomMembers(roomId);
              setMembers(updatedMembers);
            } catch (err) {
              console.error('ChatContext: Erro ao atualizar lista de membros:', err);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'chat_room_members',
            filter: `room_id=eq.${roomId}`
          },
          (payload) => {
            console.log('ChatContext: Membro removido via real-time:', payload.old);
            
            // Remover membro da lista local
            setMembers(prevMembers => 
              prevMembers.filter(member => member.user_auth_id !== payload.old.user_auth_id)
            );
          }
        )
        .subscribe((status) => {
          console.log('ChatContext: Status da subscription real-time:', status);
        });
      
      setSubscription(newSubscription);
      
      setIsLoading(false);
      console.log('ChatContext: Seleção da sala concluída com sucesso');
    } catch (err) {
      console.error('ChatContext: Erro ao selecionar sala:', err);
      setError(err instanceof Error ? err.message : 'Falha ao carregar dados da sala');
      setIsLoading(false);
      throw err;
    }
  }, [getUserAuthId, cleanupSubscription]);

  const createRoom = useCallback(async (
    name: string,
    type: ChatRoom['type'],
    projectId?: string
  ) => {
    if (!propOrgId) {
      setError('Organization ID is required');
      return null;
    }

    const userAuthId = getUserAuthId();
    if (!userAuthId) {
      setError('Usuário não autenticado');
      return null;
    }

    try {
      console.log('ChatContext: Criando nova sala', { name, type, projectId });
      const newRoom = await chatService.createChatRoom({
        name,
        organization_id: propOrgId,
        project_id: projectId,
        type
      });

      // Adicionar o usuário como membro admin da sala
      console.log('ChatContext: Adicionando usuário como admin', { roomId: newRoom.id, userAuthId });
      await chatService.addChatRoomMember({
        room_id: newRoom.id,
        user_auth_id: userAuthId,
        role: 'admin'
      });

      console.log('ChatContext: Sala criada com sucesso', newRoom);
      setRooms(prev => [...prev, newRoom]);
      setError(null);
      return newRoom;
    } catch (err) {
      console.error('ChatContext: Erro ao criar sala:', err);
      setError(err instanceof Error ? err.message : 'Falha ao criar sala de chat');
      return null;
    }
  }, [propOrgId, getUserAuthId]);

  const sendMessage = useCallback(async (content: string, parentId?: string, mentionedUserIds?: string[]) => {
    const userAuthId = getUserAuthId();
    if (!userAuthId || !currentRoom) {
      console.error('ChatContext: Cannot send message - missing user or room', { userAuthId, currentRoom });
      setError('Usuário ou sala não disponível');
      return;
    }
    
    try {
      // Verificar moderação antes de enviar a mensagem
      console.log('ChatContext: Checking message moderation');
      const moderationCheck = await moderationService.checkMessageContent(
        content, 
        currentRoom.id, 
        userAuthId
      );
      
      if (!moderationCheck.isAllowed) {
        const errorMessage = `Mensagem não permitida: ${moderationCheck.violations.join(', ')}`;
        console.warn('ChatContext: Message blocked by moderation:', moderationCheck);
        setError(errorMessage);
        
        // Para violações críticas ou múltiplas, pode ser necessário ação adicional
        if (moderationCheck.severity === 'critical' || moderationCheck.suggestedAction === 'delete') {
          console.log('ChatContext: Critical violation detected, blocking message');
          return;
        }
        
        // Para outras violações, ainda bloquear mas permitir tentar novamente
        setTimeout(() => setError(null), 5000); // Limpar erro após 5 segundos
        return;
      }
      
      console.log('ChatContext: Message passed moderation, sending', { 
        roomId: currentRoom.id, 
        userAuthId, 
        content, 
        parentId,
        mentionedUserIds 
      });
      
      const newMessage = await chatService.sendChatMessage({
        room_id: currentRoom.id,
        user_auth_id: userAuthId,
        content,
        parent_id: parentId
      });
      
      // Processar menções, se houver
      if (mentionedUserIds && mentionedUserIds.length > 0) {
        console.log('ChatContext: Processando menções para', mentionedUserIds);
        try {
          for (const mentionedUserId of mentionedUserIds) {
            await chatService.createChatMention({
              message_id: newMessage.id,
              user_auth_id: mentionedUserId,
              is_read: false
            });
          }
          console.log('ChatContext: Menções processadas com sucesso');
        } catch (mentionError) {
          console.error('ChatContext: Erro ao processar menções:', mentionError);
          // Continuamos mesmo se as menções falharem, pelo menos a mensagem foi enviada
        }
      }
      
      console.log('ChatContext: Message sent successfully', newMessage);
      // A subscription global ignora as próprias mensagens, então a prévia da
      // lista de conversas só acompanha o que eu envio se registrar aqui.
      chatPopupRef.current?.registerRoomMessage(
        currentRoom.id,
        newMessage.content,
        newMessage.created_at || new Date().toISOString()
      );
      setMessages(prev => [...prev, newMessage]);
      setError(null);
    } catch (err) {
      console.error('ChatContext: Error sending message:', err);
      setError('Falha ao enviar mensagem. Por favor, tente novamente.');
      throw err;
    }
  }, [currentRoom, getUserAuthId]);

  const sendMessageWithAttachment = useCallback(async (content: string, file: File, parentId?: string) => {
    const userAuthId = getUserAuthId();
    if (!userAuthId || !currentRoom) {
      console.error('ChatContext: Cannot send message - missing user or room', { userAuthId, currentRoom });
      setError('Usuário ou sala não disponível');
      return;
    }

    if (!propOrgId) {
      console.error('ChatContext: Cannot attach file - missing organization');
      setError('Organização não disponível');
      return;
    }
    
    try {
      console.log('ChatContext: Sending message with attachment', { roomId: currentRoom.id, userAuthId, content, fileName: file.name });

      // O arquivo sobe ANTES de a mensagem existir. Na ordem inversa, o INSERT
      // da mensagem avisava os outros clientes pelo Realtime enquanto o upload
      // ainda rodava: eles buscavam os anexos, não achavam nada (a linha de
      // `chat_attachments` só nasce no fim) e ficavam com a mensagem sem o
      // vídeo até recarregar a sala. A janela medida em produção ia de 0,8s a
      // 5,4s, sempre maior que o tempo de resposta do Realtime.
      const filePath = await chatService.uploadAttachmentFile(file, propOrgId);

      const newMessage = await chatService.sendChatMessage(
        {
          room_id: currentRoom.id,
          user_auth_id: userAuthId,
          content,
          parent_id: parentId
        },
        // O arquivo é o conteúdo desta mensagem. A de voz vai sem texto de
        // propósito; sem esta permissão ela seria recusada pela validação do
        // serviço com o áudio já no Storage.
        { permitirTextoVazio: true }
      );

      const attachment = await chatService.registerChatAttachment(newMessage.id, file, filePath);
      console.log('ChatContext: File attached successfully', attachment);
      newMessage.attachments = [attachment];

      console.log('ChatContext: Message with attachment sent successfully', newMessage);
      chatPopupRef.current?.registerRoomMessage(
        currentRoom.id,
        newMessage.content,
        newMessage.created_at || new Date().toISOString()
      );
      setMessages(prev => [...prev, newMessage]);
      setError(null);
    } catch (err) {
      // Sem `setError` aqui: o ChatRoom troca a sala inteira por uma tela de
      // erro quando o contexto tem erro, e perder a conversa por causa de um
      // anexo que falhou é pior que o problema. Quem chamou avisa em toast.
      console.error('ChatContext: Error sending message with attachment:', err);
      throw err;
    }
  }, [currentRoom, getUserAuthId, propOrgId]);

  // Editar e excluir a própria mensagem.
  //
  // A lista local é atualizada com o que o banco devolveu, e não com o que a
  // tela pediu: se o RLS recusar (mensagem de outra pessoa, ou já apagada), o
  // serviço lança e nada muda na tela. Sem `setError`: quem chama avisa em
  // toast — o ChatRoom troca a sala inteira por uma tela de erro quando o
  // contexto tem erro, e perder a conversa por causa de uma edição é pior que
  // o problema.
  const editMessage = useCallback(async (messageId: string, content: string) => {
    const atualizada = await chatService.editChatMessage(messageId, content);
    setMessages(prev => aplicarMensagemAtualizada(prev, atualizada));
  }, []);

  const deleteMessage = useCallback(async (messageId: string) => {
    const atualizada = await chatService.deleteChatMessage(messageId);
    setMessages(prev => aplicarMensagemAtualizada(prev, atualizada));
  }, []);

  const loadMoreMessages = useCallback(async () => {
    if (!currentRoom || !hasMoreMessages) return;
    
    try {
      setIsLoading(true);
      const nextPage = messagesPage + 1;
      const offset = nextPage * 50;
      
      const moreMessages = await chatService.getChatMessages(currentRoom.id, 50, offset);
      
      if (moreMessages.length === 0) {
        setHasMoreMessages(false);
      } else {
        // Adicionar mensagens mais antigas antes das atuais na ordem cronológica
        setMessages(prev => [...moreMessages, ...prev]);
        setMessagesPage(nextPage);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('ChatContext: Error loading more messages:', err);
      setError('Falha ao carregar mais mensagens');
      setIsLoading(false);
    }
  }, [currentRoom, messagesPage, hasMoreMessages]);

  const addMember = useCallback(async (
    userAuthId: string,
    role: 'admin' | 'member' | 'guest' = 'member'
  ) => {
    if (!currentRoom) return;
    
    try {
      console.log('ChatContext: Adicionando membro à sala', { 
        roomId: currentRoom.id, 
        userAuthId, 
        role, 
        roomType: currentRoom.type,
        projectId: currentRoom.project_id
      });
      
      const newMember = await chatService.addChatRoomMember({
        room_id: currentRoom.id,
        user_auth_id: userAuthId,
        role
      });
      
      console.log('ChatContext: Membro adicionado com sucesso', newMember);
      setMembers(prev => [...prev, newMember]);
    } catch (err) {
      console.error('Erro ao adicionar membro:', err);
      setError('Falha ao adicionar membro à sala');
    }
  }, [currentRoom]);

  const removeMember = useCallback(async (userAuthId: string) => {
    if (!currentRoom) return;
    
    try {
      console.log('ChatContext: Removendo membro da sala', { 
        roomId: currentRoom.id, 
        userAuthId, 
        roomType: currentRoom.type,
        projectId: currentRoom.project_id
      });
      
             await chatService.removeChatRoomMember(currentRoom.id, userAuthId);
      
      console.log('ChatContext: Membro removido com sucesso');
      setMembers(prev => prev.filter(member => member.user_auth_id !== userAuthId));
    } catch (err) {
      console.error('Erro ao remover membro:', err);
      setError('Falha ao remover membro da sala');
    }
  }, [currentRoom]);

  const isCurrentUserAdmin = useCallback(async () => {
    const userAuthId = getUserAuthId();
    if (!userAuthId || !currentRoom) return false;
    
    try {
      console.log('ChatContext: Verificando se o usuário é admin da sala', { roomId: currentRoom.id, userAuthId });
      const { data: memberData, error: memberError } = await supabase
        .from('chat_room_members')
        .select('role')
        .eq('room_id', currentRoom.id)
        .eq('user_auth_id', userAuthId)
        .single();

      if (memberError && memberError.code !== 'PGRST116') {
        console.error('ChatContext: Erro ao verificar membro:', memberError);
        throw new Error('Erro ao verificar acesso à sala');
      }

      if (!memberData) {
        console.log('ChatContext: Usuário não é membro da sala');
        return false;
      }
      
      return memberData.role === 'admin';
    } catch (err) {
      // Não usar setError aqui: isso só decide se controles de admin aparecem,
      // e o `error` global bloqueia a tela inteira do chat (ChatRoom.tsx) sem
      // nenhuma forma de voltar — uma falha nessa checagem incidental não pode
      // travar a conversa inteira.
      console.error('ChatContext: Erro ao verificar se o usuário é admin da sala:', err);
      return false;
    }
  }, [currentRoom, getUserAuthId]);

  const updateMemberRole = useCallback(async (
    userAuthId: string,
    newRole: 'admin' | 'member' | 'guest'
  ) => {
    if (!currentRoom) return;
    
    try {
      console.log('ChatContext: Atualizando papel do membro', { 
        roomId: currentRoom.id, 
        userAuthId, 
        newRole, 
        roomType: currentRoom.type,
        projectId: currentRoom.project_id
      });
      
      await chatService.updateChatRoomMemberRole(currentRoom.id, userAuthId, newRole);
      
      console.log('ChatContext: Papel do membro atualizado com sucesso');
      setMembers(prev => prev.map(member =>
        member.user_auth_id === userAuthId ? { ...member, role: newRole } : member
      ));
    } catch (err) {
      console.error('Erro ao atualizar papel do membro:', err);
      setError('Falha ao atualizar papel do membro');
    }
  }, [currentRoom]);

  const clearCurrentRoom = useCallback(() => {
    setCurrentRoom(null);
  }, []);

  // Limpar subscription ao desmontar componente ou trocar organização
  useEffect(() => {
    return () => {
      cleanupSubscription();
    };
  }, [cleanupSubscription]);

  // Limpar subscription quando organização muda
  useEffect(() => {
    cleanupSubscription();
  }, [propOrgId, cleanupSubscription]);

  const contextValue = useMemo<ChatContextType>(() => ({
    rooms,
    messages,
    error,
    loadRooms,
    createRoom,
    currentRoom,
    members,
    isLoading,
    selectRoom,
    clearCurrentRoom,
    sendMessage,
    sendMessageWithAttachment,
    editMessage,
    deleteMessage,
    loadMoreMessages,
    addMember,
    removeMember,
    updateMemberRole,
    isCurrentUserAdmin
  }), [
    rooms, messages, error, loadRooms, createRoom, currentRoom, members, isLoading, selectRoom, clearCurrentRoom, sendMessage, sendMessageWithAttachment, editMessage, deleteMessage, loadMoreMessages, addMember, removeMember, updateMemberRole, isCurrentUserAdmin
  ]);

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
} 